import type { MouseEvent } from 'react';
import { isEqual, isUndefined, omit, omitBy } from 'lodash-es';

import type { DataTableSelectionState } from 'components/data-table';

import type {
  SourceRow,
  SourceSelectAllParams,
  SourceSelectionChange,
} from './types';

/**
 * Synthetic id stuffed into `toggledNodes` so DataTable's header renders
 * indeterminate when the selection is non-empty but no row on the visible page
 * is in it (the user selected rows, then searched or paged away). The sentinel
 * matches no real row id, so cells render unchecked and the diff ignores it.
 * Same trick as the Step 1 assets table's `MIXED_SENTINEL`.
 */
export const MIXED_SENTINEL = '__hidden_source_selection__';

/**
 * The last select-all that went through on a table: the scope it ran under
 * and every id it returned. It is what lets the header claim "all of them"
 * for a match set too big to see on one page.
 */
export interface SelectAllSnapshot {
  params: SourceSelectAllParams;
  ids: number[];
}

/**
 * Whether the last select-all still describes the selection: it ran under
 * the scope the table is showing now, and every id it returned is still
 * selected. A per-row untick, a search or filter change, or a deselect-all
 * each break it.
 *
 * Params are compared with unset keys dropped — clearing the search box
 * leaves `text: undefined` where there was no key before, and that is the
 * same scope. Sort is left out: reordering the table doesn't change which
 * rows match.
 *
 * @param {SelectAllSnapshot | null} snapshot - Last successful select-all
 * @param {SourceSelectAllParams} params - The table's current scope
 * @param {Set<number>} selectedIds - Every selected id, across pages
 * @return {boolean} True when every match of the current scope is selected
 */
export const selectAllCovers = (
  snapshot: SelectAllSnapshot | null,
  params: SourceSelectAllParams,
  selectedIds: Set<number>
): boolean =>
  snapshot !== null &&
  isEqual(matchScope(snapshot.params), matchScope(params)) &&
  snapshot.ids.every((id) => selectedIds.has(id));

const matchScope = (params: SourceSelectAllParams) =>
  omitBy(omit(params, ['sortAttribute', 'sortDirection']), isUndefined);

/**
 * Map the form's selection (the source of truth, which spans pages) onto
 * DataTable's `{ selectAll, toggledNodes }` model for the rows currently on
 * screen. Server-driven DataTable reads the header as checked when
 * `selectAll && toggledNodes.length === 0`, and indeterminate whenever
 * `toggledNodes` is non-empty, which gives:
 *
 *   - every MATCHING row selected → checked
 *   - some rows selected → indeterminate, the visible ones ticked
 *   - selection lives entirely off-page → indeterminate via the sentinel
 *   - nothing selected anywhere → unchecked
 *
 * `selectAll: true` is reserved for a selection that covers the whole match
 * set, not merely the visible page: in this model it means "all of them", so
 * claiming it for 25-of-10,000 would both lie to the user and turn their next
 * header click into a deselect-everything. Same rule as the Step 1 assets
 * table, which only sends `selectAll: true` when the BE reports `'all'`.
 *
 * Only two things establish it. Either the whole match set is on this page
 * and every row of it is ticked, or a select-all for the current scope is
 * still intact (`selectAllCovered`, from {@link selectAllCovers}). Counting
 * selected ids against `totalCount` does not: ids picked under an earlier
 * search inflate the count, so the header would read checked while later
 * pages still hold unselected matches.
 *
 * @param {number[]} visibleIds - Ids of the rows on the current page, in order
 * @param {Set<number>} selectedIds - Every selected id, across pages
 * @param {number} totalCount - Rows matching the current search / filters
 * @param {boolean} selectAllCovered - The last select-all still holds
 * @return {DataTableSelectionState} Controlled selection for DataTable
 */
export const sourceSelectionState = (
  visibleIds: number[],
  selectedIds: Set<number>,
  totalCount: number,
  selectAllCovered = false
): DataTableSelectionState => {
  const visibleSelected = visibleIds.filter((id) => selectedIds.has(id));
  // Even with the whole match set covered, the visible rows must actually be
  // the selected ones: narrowing the search shrinks the match set while the
  // selection keeps its off-page rows, so selecting one entity and then
  // searching for the next one — which usually matches a single row — must not
  // render that row ticked and swallow every click on it.
  const everyVisibleSelected =
    visibleIds.length > 0 && visibleSelected.length === visibleIds.length;
  if (
    everyVisibleSelected &&
    (visibleIds.length === totalCount || selectAllCovered)
  ) {
    return { selectAll: true, toggledNodes: [] };
  }
  if (visibleSelected.length > 0) {
    return { selectAll: false, toggledNodes: visibleSelected.map(String) };
  }
  return {
    selectAll: false,
    toggledNodes: selectedIds.size > 0 ? [MIXED_SENTINEL] : [],
  };
};

/**
 * A header click vs a per-row click. DataTable always clears `toggledNodes`
 * on a header click, so any flip of `selectAll` against the state we fed it
 * IS a header click — a per-row click leaves `selectAll` alone and only edits
 * `toggledNodes`. Same detection as the Step 1 assets table.
 *
 * @param {DataTableSelectionState} next - What DataTable just reported
 * @param {DataTableSelectionState} current - What we fed it this render
 * @return {boolean} True when the header checkbox was the thing clicked
 */
export const isHeaderClick = (
  next: DataTableSelectionState,
  current: DataTableSelectionState
): boolean => next.selectAll !== current.selectAll;

/**
 * Resolve a per-row click into ids to add and drop, scoped to the visible
 * page. Rows outside the page are untouched — a click can only ever change
 * the row that was clicked, but comparing the whole page against the current
 * selection keeps the reducer's `selectAll`-with-exceptions shape honest.
 *
 * @param {DataTableSelectionState} next - What DataTable just reported
 * @param {number[]} visibleIds - Ids of the rows on the current page
 * @param {Set<number>} selectedIds - Every selected id, across pages
 * @return {Object} `addedIds` / `removedIds` for the visible page
 */
export const visibleSelectionDiff = (
  next: DataTableSelectionState,
  visibleIds: number[],
  selectedIds: Set<number>
): { addedIds: number[]; removedIds: number[] } => {
  const toggled = new Set(
    next.toggledNodes.filter((id) => id !== MIXED_SENTINEL)
  );
  const addedIds: number[] = [];
  const removedIds: number[] = [];

  for (const id of visibleIds) {
    const intended = next.selectAll
      ? !toggled.has(String(id))
      : toggled.has(String(id));
    if (intended && !selectedIds.has(id)) {
      addedIds.push(id);
    } else if (!intended && selectedIds.has(id)) {
      removedIds.push(id);
    }
  }

  return { addedIds, removedIds };
};

/**
 * Click handler for the name link of a multi-select source row. A plain left
 * click toggles the row (the row's own click handler does that) instead of
 * navigating, so the name reads as a link but behaves as part of the row —
 * picking sources is what this table is for, and the row is already clickable.
 * A modifier click is a deliberate "open in a new tab", so it keeps the
 * browser's behavior and does NOT also toggle. A middle click never reaches a
 * React `onClick`, so it opens the tab untouched.
 *
 * Keyboard activation follows the link the same way: Enter on a focused link
 * dispatches a click with `detail === 0`, and swallowing that would announce an
 * element as a link that can never be followed without a mouse. Keyboard users
 * select through the checkbox column instead.
 *
 * @param {MouseEvent} event - Click on the name link
 * @return {void}
 */
export const handleSourceNameClick = (event: MouseEvent<HTMLElement>): void => {
  const opensLink =
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey ||
    event.detail === 0;
  if (opensLink) {
    event.stopPropagation();
    return;
  }
  event.preventDefault();
};

/**
 * Keep a cell's click from reaching the row. For the cells of a
 * `rowClickSelection` table whose links point somewhere OTHER than the row's
 * own resource (an entity id link, a package's seller links) — following one
 * must not also change the selection.
 *
 * @param {MouseEvent} event - Click inside the cell
 * @return {void}
 */
export const stopRowClick = (event: MouseEvent<HTMLElement>): void =>
  event.stopPropagation();

/**
 * Apply a table's selection delta to the form's source list. Additions append
 * in report order (so the title and Step 1 panel list sources in table order);
 * an id already in the list keeps its existing row, so re-selecting a source
 * never reorders it.
 *
 * @param {SourceRow[]} current - Current form value
 * @param {SourceSelectionChange} change - Delta reported by the table
 * @return {SourceRow[]} Next form value
 */
export const mergeSourceSelection = (
  current: SourceRow[],
  change: SourceSelectionChange
): SourceRow[] => {
  const removed = new Set(change.removed);
  const kept = current.filter((source) => !removed.has(source.id));
  const keptIds = new Set(kept.map((source) => source.id));

  return [...kept, ...change.added.filter((source) => !keptIds.has(source.id))];
};
